import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Grid,
    Typography,
} from '../../base-components';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { ReactNode } from 'react';

export interface IRadioButtonItem {
    key: string;
    description?: string;
    label: string;
    url?: string;
    disabled?: boolean;
    imageComponent?: ReactNode;
    additionalContent?: ReactNode;
    tooltipText?: string;
}

interface IRadioButtonGroupProps<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    buttonsContent: Array<IRadioButtonItem>;
    disabled?: boolean;
    spacing?: number;
    dataTestId?: string;
    direction?: 'row' | 'column';
    size?: 'small' | 'medium';
}

const FormRadioButtonGroup = <F extends FieldValues>(
    props: IRadioButtonGroupProps<F>
) => {
    const {
        name,
        buttonsContent,
        spacing = 0,
        dataTestId,
        disabled,
        control,
        direction = 'row',
        size = 'medium',
    } = props;

    return (
        <Controller
            data-testid={`form-radio-selector-${name}`}
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <>
                    <RadioGroup
                        sx={{ width: '100%' }}
                        value={field.value}
                        onChange={field.onChange}
                        data-testid={dataTestId || name}
                    >
                        <Grid container spacing={spacing} direction={direction}>
                            {buttonsContent.map((item: IRadioButtonItem) => (
                                <Grid
                                    key={item.key}
                                    item
                                    xs={direction === 'column' ? 12 : 'auto'}
                                >
                                    <FormControlLabel
                                        sx={{ width: '100%' }}
                                        label={item.label}
                                        disabled={item.disabled || disabled}
                                        data-testid={`FormRadioButtonGroup-gridButton-${name}-${item.key}`}
                                        control={
                                            <Radio
                                                size={size}
                                                disabled={item.disabled || disabled}
                                                value={item.key}
                                                checked={field.value === item.key}
                                            />
                                        }
                                    />
                                    {field.value === item.key &&
                                        item.additionalContent}
                                </Grid>
                            ))}
                        </Grid>
                    </RadioGroup>
                    {error?.message && (
                        <Typography variant="caption" color="error">
                            {error?.message}
                        </Typography>
                    )}
                </>
            )}
        />
    );
};

export default FormRadioButtonGroup;
